import React from "react";
import { Box, Chip, Grid, Stack, Typography } from "@mui/material";
import {
  Event,
  NotificationsActive,
  Star,
  Timeline,
} from "@mui/icons-material";
import { PageIntro, PageShell, SurfaceCard } from "../ui/AppPrimitives";

const features = [
  {
    icon: Event,
    title: "Centralized dates",
    description: "Keep exams, appointments, milestones, and deadlines in one polished workspace.",
  },
  {
    icon: Star,
    title: "Priority focus",
    description: "Separate critical dates from background noise and act sooner with confidence.",
  },
  {
    icon: Timeline,
    title: "Clear timeline",
    description: "See upcoming events in grouped views, calendar layouts, and smooth celebration flows.",
  },
  {
    icon: NotificationsActive,
    title: "Future ready",
    description: "Structured to support reminders, sync, and richer guidance as the product grows.",
  },
];

export default function About() {
  return (
    <PageShell maxWidth="xl">
      <PageIntro
        eyebrow="About VenTush"
        title="A modern date tracker built around calm, clarity, and trust."
        subtitle="VenTush helps people organize the moments that matter, from deadlines and medical visits to anniversaries and celebrations."
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SurfaceCard sx={{ p: { xs: 2.5, md: 3.5 }, height: "100%" }}>
            <Chip label="Vision" color="primary" sx={{ mb: 1.5 }} />
            <Typography variant="h3" sx={{ mb: 1.2 }}>
              A digital timeline that feels dependable, not noisy.
            </Typography>
            <Typography color="text.secondary">
              We want important life dates to feel easy to manage. The product is
              designed to reduce mental overhead and make preparation feel calm,
              visible, and within reach.
            </Typography>
          </SurfaceCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SurfaceCard sx={{ p: { xs: 2.5, md: 3.5 }, height: "100%" }}>
            <Chip label="Mission" color="secondary" sx={{ mb: 1.5 }} />
            <Typography variant="h3" sx={{ mb: 1.2 }}>
              Simpler planning for real-life moments.
            </Typography>
            <Typography color="text.secondary">
              VenTush brings structure to exams, health visits, legal timelines,
              personal milestones, and recurring celebrations through a clean,
              distraction-light experience.
            </Typography>
          </SurfaceCard>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3.5 }}>
        <Typography variant="h3" sx={{ mb: 2 }}>
          Why it feels different
        </Typography>
        <Grid container spacing={3}>
          {features.map(({ icon: Icon, title, description }) => (
            <Grid item xs={12} sm={6} lg={3} key={title}>
              <SurfaceCard sx={{ p: 3, height: "100%" }}>
                <Stack spacing={1.5}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: 3.5,
                      bgcolor: "rgba(37, 99, 235, 0.10)",
                      color: "primary.main",
                    }}
                  >
                    <Icon />
                  </Box>
                  <Typography variant="h4">{title}</Typography>
                  <Typography color="text.secondary">{description}</Typography>
                </Stack>
              </SurfaceCard>
            </Grid>
          ))}
        </Grid>
      </Box>
    </PageShell>
  );
}
